<template>
  <v-card
    id="staff"
    :border="$role == 'specialist'"
    flat
    tile
  >
    <v-card-title class="tab-title d-flex">
      <v-btn
        v-if="showProviderProfileNavBtn"
        @click="$emit('toggleProviderProfileNav')"
        :aria-label="$bct('Toggle provider profile navigation')"
        data-cy="provider_profile_nav_icon"
        icon="menu_open"
        variant="text"
      />
      <h2 class="v-card-title py-0 px-0 d-flex align-center">
        {{ $bct('Staff members') }}
      </h2>
      <v-spacer />
      <StandardButton
        @click="draft({})"
        data-cy="add_staff_photo"
        data-testid="staff-members-add-button"
        role="create"
        text="Add"
      />
    </v-card-title>
    <v-divider />
    <v-card-text class="py-4">
      <v-row>
        <v-col>{{ $bct(STAFF_MEMBER_INTRO_TEXT) }}</v-col>
      </v-row>
      <v-row>
        <v-col
          v-for="member in staffMembers"
          :key="member.id"
          cols="12"
        >
          <v-card
            data-testid="staff-member-card"
            border
            flat
          >
            <v-card-text>
              <template v-if="member.attachment_url">
                <v-row>
                  <v-col class="d-flex justify-center">
                    <div style="min-height: 250px">
                      <img
                        :alt="`${$bct('Photo of')} ${member.name}`"
                        :src="member.attachment_url"
                        class="rounded mxw-100pc mxh-250"
                      />
                    </div>
                  </v-col>
                </v-row>
              </template>
              <v-row>
                <v-col>
                  <div
                    v-text="member.name"
                    class="fs-18 fw-600"
                  />
                </v-col>
              </v-row>
              <v-divider class="my-3" />
              <v-row>
                <v-col>
                  <div class="fs-16 fw-500 c-black">
                    {{ $bct(member.bio) }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <StandardButton
                @click="destroy(member.id)"
                data-cy="remove_staff"
                data-testid="staff-member-remove-button"
                role="destructive"
                text="Remove"
              />
              <v-spacer />
              <StandardButton
                @click="draft(member)"
                data-cy="edit_staff"
                data-testid="staff-member-edit-button"
                role="secondary"
                text="Edit"
              />
            </v-card-actions>
          </v-card>
        </v-col>

        <v-dialog
          v-model="editorIsVisible"
          max-width="530px"
        >
          <v-card
            data-testid="staff-member-editor-dialog"
            border
            flat
            tile
          >
            <v-card-title>
              <span>
                {{ $bct(staffMember.id ? 'Edit staff member' : 'New staff member') }}
              </span>
            </v-card-title>

            <v-divider class="mb-4" />

            <v-card-text>
              <v-row>
                <v-col>
                  <dashboard
                    :props="uppyProps"
                    :uppy="uppy"
                  />
                </v-col>
              </v-row>

              <v-row>
                <LabeledTextfield
                  v-model="staffMember.name"
                  aria-label="Staff member name"
                  data-cy="staff_member_name"
                  data-testid="staff-member-name-field"
                  message="Name"
                />

                <LabeledTextarea
                  v-model="staffMember.bio"
                  aria-label="Staff member bio"
                  data-cy="staff_member_bio"
                  data-testid="staff-member-bio-field"
                  message="Short bio"
                />
              </v-row>
            </v-card-text>

            <v-divider class="mb-4" />

            <v-card-actions>
              <StandardButton
                @click="cancel()"
                role="secondary"
                text="Cancel"
              />
              <v-spacer />
              <StandardButton
                @click="save()"
                :disabled="!staffMember.name || !staffMember.bio"
                data-cy="save_staff"
                text="Save"
              />
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { Dashboard } from '@uppy/vue';
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';

import LabeledTextarea from '@/shared/components/form/LabeledTextarea.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useUppyUtils from '@/shared/composables/useUppyUtils';

const STAFF_MEMBER_INTRO_TEXT =
  'Families are interested in learning about those who will be caring ' +
  'for their children. Upload a photo and a short bio about each of your team members.';

const emit = defineEmits(['progress', 'toggleProviderProfileNav']);

const props = defineProps({
  providerId: {
    type: String,
    default: null,
  },
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const $bct = inject('translator');
const { api: Api } = useApi();
const eventBus = useEventBus();
const { getUploadAPIEndpoint, createUppyPropsWithLocale } = useUppyUtils();

const attachment = ref(null);
const editorIsVisible = ref(false);
const staffMember = reactive({});
const staffMembers = ref([]);
const uppy = ref(null);
const processing = ref(false);

watch(() => props.providerId, load, { immediate: true });

const { uppyProps, setupUppyWithTranslations } = createUppyPropsWithLocale();

onBeforeUnmount(() => {
  if (uppy.value) {
    uppy.value.close();
  }
});

onMounted(() => {
  setupUppyWithTranslations(() => {
    uppy.value = new Uppy().use(XHRUpload, {
      endpoint: getUploadAPIEndpoint(),
    });

    uppy.value.on('upload-success', async (file, evt) => {
      const att = {
        file: {
          id: evt.body.id, // remove the Shrine storage prefix
          storage: 'cache',
          metadata: {
            size: file.size,
            filename: file.name,
            mime_type: file.type,
          },
        },
        category: 'image',
      };

      const resp = await Api.member.attachment.create(att);
      if (resp?.status !== 201) return uppy.value.cancelAll();
      attachment.value = resp.data;
      return attachment.value;
    });
  });
});

function cancel() {
  Object.assign(staffMember, {});
  attachment.value = null;
  editorIsVisible.value = false;
}

async function confirmAttachment(staffMemberId) {
  processing.value = true;
  const params = {
    owner_type: 'StaffMember',
    owner_id: staffMemberId,
  };
  const resp = await Api.member.attachment.update(attachment.value.id, params);
  if (resp?.status !== 200) return;

  saved();
}

function destroy(staffMemberId) {
  Api.provider.staff_member.destroy(props.providerId, staffMemberId, () => {
    eventBus.chime('Removed');
    load();
  });
}

function draft(member) {
  Object.assign(staffMember, member);
  editorIsVisible.value = true;
}

function load() {
  Api.publicApi.provider.staff_member.index(props.providerId, (resp) => {
    staffMembers.value = resp.data;
    emit('progress', staffMembers.value.length > 0 ? 100 : 0);
  });
}

function save() {
  Api.provider.staff_member.updateOrCreate(
    props.providerId,
    staffMember,
    (resp) => {
      if (attachment.value) {
        void confirmAttachment(resp.data.id);
      } else {
        saved();
      }
    },
    (errors) => {
      eventBus.chime(errors.response.data.errors.join('. '));
    },
  );
}

function saved() {
  processing.value = false;
  eventBus.chime('Staff member saved!');
  Object.assign(staffMember, {});
  editorIsVisible.value = false;
  load();
}
</script>
